import React, { useEffect, useRef, useState } from 'react';
import SignatureCanvas from 'react-signature-canvas';
import { Button, Form } from 'react-bootstrap';

interface SignaturePadProps {
  onSignatureChange: (dataUrl: string | null, inputType: 'draw' | 'type') => void;
}

const SIGNATURE_INPUT_TYPE_DRAW = 'draw';
const SIGNATURE_INPUT_TYPE_TYPE = 'type';
const SIGNATURE_CANVAS_WIDTH = 500;
const SIGNATURE_CANVAS_HEIGHT = 150;
const SIGNATURE_IMAGE_MIME = 'image/jpeg';

const SignaturePad: React.FC<SignaturePadProps> = ({ onSignatureChange }) => {
  const sigCanvasRef = useRef<SignatureCanvas>(null);
  const canvasContainerRef = useRef<HTMLDivElement>(null);
  const [inputMode] = useState<'draw' | 'type'>(SIGNATURE_INPUT_TYPE_DRAW);
  const [typedSignature, setTypedSignature] = useState('');
  const [canvasWidth, setCanvasWidth] = useState<number>(SIGNATURE_CANVAS_WIDTH);

  useEffect(() => {
    const updateCanvasWidth = () => {
      const container = canvasContainerRef.current;
      if (!container) return;
      const nextWidth = Math.max(280, Math.floor(container.clientWidth));
      setCanvasWidth(prevWidth => (prevWidth === nextWidth ? prevWidth : nextWidth));
    };

    updateCanvasWidth();

    const container = canvasContainerRef.current;
    const resizeObserver = typeof ResizeObserver !== 'undefined'
      ? new ResizeObserver(updateCanvasWidth)
      : null;

    if (container && resizeObserver) {
      resizeObserver.observe(container);
    }

    window.addEventListener('resize', updateCanvasWidth);
    return () => {
      window.removeEventListener('resize', updateCanvasWidth);
      if (resizeObserver) {
        resizeObserver.disconnect();
      }
    };
  }, []);

  const exportDrawnSignatureAsJpeg = (): string | null => {
    if (!sigCanvasRef.current) {
      return null;
    }

    const sourceCanvas = sigCanvasRef.current.getCanvas();
    const flattenedCanvas = document.createElement('canvas');
    flattenedCanvas.width = sourceCanvas.width;
    flattenedCanvas.height = sourceCanvas.height;
    const context = flattenedCanvas.getContext('2d');

    if (!context) {
      return null;
    }

    context.fillStyle = '#ffffff';
    context.fillRect(0, 0, flattenedCanvas.width, flattenedCanvas.height);
    context.drawImage(sourceCanvas, 0, 0);

    return flattenedCanvas.toDataURL(SIGNATURE_IMAGE_MIME, 0.95);
  };

  const handleClear = () => {
    if (inputMode === SIGNATURE_INPUT_TYPE_DRAW && sigCanvasRef.current) {
      sigCanvasRef.current.clear();
      onSignatureChange(null, SIGNATURE_INPUT_TYPE_DRAW);
    } else {
      setTypedSignature('');
      onSignatureChange(null, SIGNATURE_INPUT_TYPE_TYPE);
    }
  };

  const handleDrawEnd = () => {
    if (sigCanvasRef.current && !sigCanvasRef.current.isEmpty()) {
      const dataUrl = exportDrawnSignatureAsJpeg();
      onSignatureChange(dataUrl, SIGNATURE_INPUT_TYPE_DRAW);
    }
  };

  const handleTypedSignatureChange = (value: string) => {
    setTypedSignature(value);
    if (value.trim().length > 0) {
      // Generate an image from typed text using canvas
      const canvas = document.createElement('canvas');
      canvas.width = SIGNATURE_CANVAS_WIDTH;
      canvas.height = SIGNATURE_CANVAS_HEIGHT;
      const ctx = canvas.getContext('2d');
      if (ctx) {
        ctx.fillStyle = '#ffffff';
        ctx.fillRect(0, 0, canvas.width, canvas.height);
        ctx.fillStyle = '#000000';
        ctx.font = 'italic 40px "Brush Script MT", "Segoe Script", cursive';
        ctx.textBaseline = 'middle';
        ctx.fillText(value, 20, canvas.height / 2);
        const dataUrl = canvas.toDataURL(SIGNATURE_IMAGE_MIME, 0.95);
        onSignatureChange(dataUrl, SIGNATURE_INPUT_TYPE_TYPE);
      }
    } else {
      onSignatureChange(null, SIGNATURE_INPUT_TYPE_TYPE);
    }
  };

  return (
    <div>
      {inputMode === SIGNATURE_INPUT_TYPE_DRAW ? (
        <div ref={canvasContainerRef}>
          <div
            style={{
              border: '2px solid #dee2e6',
              borderRadius: '8px',
              backgroundColor: '#ffffff',
              cursor: 'crosshair'
            }}
          >
            <SignatureCanvas
              ref={sigCanvasRef}
              canvasProps={{
                width: canvasWidth,
                height: SIGNATURE_CANVAS_HEIGHT,
                className: 'signature-canvas',
                style: { width: `${canvasWidth}px`, height: `${SIGNATURE_CANVAS_HEIGHT}px` }
              }}
              penColor="black"
              backgroundColor="#ffffff"
              onEnd={handleDrawEnd}
            />
          </div>
          <div className="d-flex justify-content-between align-items-center mt-2">
            <small className="text-muted">Draw your signature above</small>
            <Button variant="outline-secondary" size="sm" onClick={handleClear}>
              Clear
            </Button>
          </div>
        </div>
      ) : (
        <div>
          <Form.Control
            type="text"
            placeholder="Type your full name as signature"
            value={typedSignature}
            onChange={(e) => handleTypedSignatureChange(e.target.value)}
            style={{
              fontFamily: '"Brush Script MT", "Segoe Script", cursive',
              fontSize: '28px',
              fontStyle: 'italic',
              padding: '20px 15px',
              height: '80px'
            }}
          />
          {typedSignature && (
            <div className="mt-2 p-3 border rounded bg-white text-center">
              <span style={{
                fontFamily: '"Brush Script MT", "Segoe Script", cursive',
                fontSize: '36px',
                fontStyle: 'italic'
              }}>
                {typedSignature}
              </span>
            </div>
          )}
          <div className="d-flex justify-content-between align-items-center mt-2">
            <small className="text-muted">Type your name to create a signature</small>
            <Button variant="outline-secondary" size="sm" onClick={handleClear}>
              Clear
            </Button>
          </div>
        </div>
      )}
    </div>
  );
};

export default SignaturePad;
